import { buttonStyle } from 'components/buttons/buttonStyle';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput.tsx';
import { DataTable } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useUpdateConversionRateConfigurationMutation } from 'store/api/tenants.ts';
import { ConversionRateConfigurationUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

const PRECISION_FIELDS: { name: keyof ConversionRateConfigurationUpdateDto; labelId: string; max: number }[] = [
    { name: 'conversionRatePrecision', labelId: 'configuration.conversion-rate-precision', max: 8 },
    { name: 'invoicingPaymentCurrencyPrecision', labelId: 'configuration.payment-currency-precision', max: 8 },
    { name: 'invoicingBaseCurrencyPrecision', labelId: 'configuration.base-currency-precision', max: 8 },
    { name: 'invoicingInvoiceCurrencyPrecision', labelId: 'configuration.invoice-currency-precision', max: 20 },
];

const CurrencyPrecision = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateConversionRateConfiguration] = useUpdateConversionRateConfigurationMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);

    const { handleSubmit, control } = useForm<ConversionRateConfigurationUpdateDto>({
        defaultValues: {
            conversionRatePrecision: tenantConfiguration.conversionRatePrecision,
            invoicingPaymentCurrencyPrecision: tenantConfiguration.invoicingPaymentCurrencyPrecision,
            invoicingBaseCurrencyPrecision: tenantConfiguration.invoicingBaseCurrencyPrecision,
            invoicingInvoiceCurrencyPrecision: tenantConfiguration.invoicingInvoiceCurrencyPrecision,
        },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.decimal-precision-for-conversions'),
                id: 'decimal-precision-for-conversions',
                enableSorting: false,
                cell: () => (
                    <div className="flex w-full">
                        <div className="flex w-1/3 flex-col gap-2">
                            {PRECISION_FIELDS.map(({ name, labelId, max }) => (
                                <div key={name} className="flex items-center justify-between">
                                    <p className="text-body2">
                                        <FormattedMessage id={labelId} />
                                    </p>
                                    <NumberInput
                                        control={control}
                                        name={name}
                                        inputProps={{ min: 0, max, 'aria-label': t(labelId) }}
                                        required
                                        disabled={!hasUpdatePermission}
                                        wrapperClassName="w-20"
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="ml-10 flex w-1/3 items-center">
                            <Button
                                {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-8')}
                                onClick={() => {
                                    void handleSubmit((data) => updateConversionRateConfiguration(data))();
                                }}
                                disabled={!hasUpdatePermission}
                            >
                                <FormattedMessage id="save" />
                            </Button>
                        </div>
                    </div>
                ),
            },
        ],
        [control, handleSubmit, hasUpdatePermission, updateConversionRateConfiguration]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.currency-precision" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-currency-precision"
            />
        </>
    );
};

export default CurrencyPrecision;
